import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Alert
} from '@mui/material';
import {
  Place as SiteIcon,
  AttachMoney as PriceIcon,
  Event as CalendarIcon,
  CheckCircle as AvailableIcon,
  Cancel as OccupiedIcon,
  Build as MaintenanceIcon,
  ElectricBolt as ElectricIcon,
  Water as WaterIcon,
  Wifi as WifiIcon,
  LocalFireDepartment as FirepitIcon,
  Pets as PetIcon,
  Park as ShadeIcon,
  ShowerOutlined as SewerIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

interface SiteDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  siteDetails: any;
}

const SiteDetailsDialog: React.FC<SiteDetailsDialogProps> = ({
  open,
  onClose,
  siteDetails
}) => {
  if (!siteDetails) return null;

  const { site, availability, mapData } = siteDetails;

  const getAmenityIcon = (amenity: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      'Electricity': <ElectricIcon />,
      'Water': <WaterIcon />,
      'Sewer': <SewerIcon />,
      'WiFi': <WifiIcon />,
      'Fire Pit': <FirepitIcon />,
      'Pet Friendly': <PetIcon />,
      'Shade': <ShadeIcon />
    };
    return icons[amenity] || <SiteIcon />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'occupied':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <AvailableIcon />;
      case 'occupied':
        return <OccupiedIcon />;
      case 'maintenance':
        return <MaintenanceIcon />;
      default:
        return <SiteIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Site {site.siteNumber}
          </Typography>
          <Chip
            icon={<>{getStatusIcon(availability.currentStatus)}</>}
            label={availability.currentStatus}
            color={getStatusColor(availability.currentStatus)}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Site Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Site Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <SiteIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Site Type"
                  secondary={site.type}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PriceIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Nightly Rate"
                  secondary={`$${site.pricePerNight}`}
                />
              </ListItem>
              {site.maxOccupancy && (
                <ListItem>
                  <ListItemText
                    primary="Max Occupancy"
                    secondary={`${site.maxOccupancy} people`}
                  />
                </ListItem>
              )}
              {site.maxLength && (
                <ListItem>
                  <ListItemText
                    primary="Max RV Length"
                    secondary={`${site.maxLength} ft`}
                  />
                </ListItem>
              )}
            </List>
          </Grid>

          {/* Amenities */}
          {site.amenities && site.amenities.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Amenities
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {site.amenities.map((amenity: string) => (
                  <Chip
                    key={amenity}
                    icon={<>{getAmenityIcon(amenity)}</>}
                    label={amenity}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>
          )}

          {/* Availability */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Availability
            </Typography>
            {availability.currentStatus === 'available' ? (
              <Alert severity="success" icon={<AvailableIcon />}>
                This site is currently available for booking
              </Alert>
            ) : (
              <Box>
                <Alert severity="info" icon={<CalendarIcon />}>
                  Next available: {dayjs(availability.nextAvailable).format('MMM D, YYYY')}
                </Alert>
                
                {availability.upcomingReservations && availability.upcomingReservations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Upcoming Reservations:
                    </Typography>
                    <List dense>
                      {availability.upcomingReservations.map((reservation: any, index: number) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${dayjs(reservation.checkIn).format('MMM D')} - ${dayjs(reservation.checkOut).format('MMM D, YYYY')}`}
                            secondary={`${reservation.guest?.firstName || 'Guest'} ${reservation.guest?.lastName || ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Grid>

          {/* Map Position */}
          {mapData && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Map Location
              </Typography>
              <Typography variant="body2">
                Position: ({Math.round(mapData.position.x)}, {Math.round(mapData.position.y)})
              </Typography>
              <Typography variant="body2">
                Size: {mapData.dimensions.width} x {mapData.dimensions.height}
              </Typography>
            </Grid>
          )}

          {/* Description */}
          {site.description && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2">
                {site.description}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {availability.currentStatus === 'available' && (
          <Button variant="contained" color="primary">
            Book This Site
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SiteDetailsDialog;